/** Pipeline oynalari: vakansiya tanlash (Submitted / Hired) va rad etish (sabab majburiy). */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiError } from "@/lib/api";
import { dayjs, cn } from "@/lib/utils";
import { useAppSettings, useOpenJobs } from "@/features/drivers/api";
import type { DriverListItem, DriverStatus } from "@/features/drivers/types";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Spinner } from "@/components/ui/misc";

export interface MoveToJobInput {
  job: number;
  note: string;
  start_date?: string;
}

export interface RejectInput {
  status: "rejected" | "not_interested";
  reason: string;
  note: string;
}

function fieldError(err: ApiError | null, key: string): string | undefined {
  return err?.fields?.[key]?.join(" ");
}

/** "Move to Submitted / Background check / Orientation / Hired": qaysi vakansiya? */
export function MoveToJobDialog({
  driver,
  target,
  onClose,
  onSubmit,
}: {
  driver: DriverListItem | null;
  target: DriverStatus;
  onClose: () => void;
  onSubmit: (input: MoveToJobInput) => Promise<ApiError | null>;
}) {
  const { t } = useTranslation("pipeline");
  const { t: tc } = useTranslation();
  const open = driver !== null;
  const jobs = useOpenJobs(open);
  const needsStart = target === "hired";
  const [jobId, setJobId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  // Oyna ochilganda: driverning joriy vakansiyasi ochiq bo'lsa, u tanlangan keladi
  useEffect(() => {
    if (!driver) return;
    const current = driver.current_job;
    setJobId(current && current.status === "open" ? current.id : null);
    setNote("");
    setStartDate(dayjs().format("YYYY-MM-DD"));
    setError(null);
  }, [driver]);

  async function submit() {
    if (!jobId) return;
    setBusy(true);
    setError(null);
    const err = await onSubmit({ job: jobId, note, start_date: needsStart ? startDate : undefined });
    setBusy(false);
    if (err) setError(err);
  }

  const list = jobs.data ?? [];

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={t("move.title", { status: tc(`status.${target}`) })}
      description={driver ? t("move.which", { name: driver.full_name }) : undefined}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {tc("actions.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={!jobId}>
            {needsStart ? t("move.markHired") : t("move.submit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          {jobs.isPending ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : list.length === 0 ? (
            <div className="rounded-[10px] border border-dashed border-border px-3 py-4 text-center text-[12px] text-muted">
              {t("move.noJobs")}
            </div>
          ) : (
            <div className="max-h-[260px] space-y-1.5 overflow-y-auto pr-1" role="radiogroup">
              {list.map((j) => {
                const active = j.id === jobId;
                return (
                  <label
                    key={j.id}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-[10px] border px-3 py-2 text-[13px]",
                      active ? "border-primary bg-primary-soft/50" : "border-border hover:bg-fill/60",
                    )}
                  >
                    <input type="radio" name="job" className="accent-primary" checked={active} onChange={() => setJobId(j.id)} />
                    <span>
                      <span className="font-medium">{j.title}</span>
                      <span className="text-muted"> · {j.carrier_name}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <p className="mt-1.5 text-[11px] text-muted">{t("move.onlyOpen")}</p>
          {fieldError(error, "job") && <p className="mt-1 text-[12px] text-danger">{fieldError(error, "job")}</p>}
        </div>
        {needsStart && (
          <Field label={t("move.startDate")} required error={fieldError(error, "start_date")}>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" />
          </Field>
        )}
        <Field label={needsStart ? t("move.evidence") : t("move.note")} hint={needsStart ? t("move.hiredHint") : undefined}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={needsStart ? t("move.placeholderHired") : t("move.placeholderNote")} rows={2} />
        </Field>
        {error && !fieldError(error, "job") && !fieldError(error, "start_date") && (
          <div role="alert" className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
            {error.message}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Rad etish zonasi: Rejected yoki Not interested, sabab majburiy */
export function RejectDialog({
  driver,
  onClose,
  onSubmit,
}: {
  driver: DriverListItem | null;
  onClose: () => void;
  onSubmit: (input: RejectInput) => Promise<ApiError | null>;
}) {
  const { t } = useTranslation("pipeline");
  const { t: tc } = useTranslation();
  const open = driver !== null;
  const settings = useAppSettings();
  const [status, setStatus] = useState<RejectInput["status"]>("rejected");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!driver) return;
    setStatus("rejected");
    setReason("");
    setNote("");
    setError(null);
  }, [driver]);

  const reasons = (status === "rejected" ? settings.data?.rejection_reasons : settings.data?.lost_reasons) ?? [];

  async function submit() {
    if (!reason) return;
    setBusy(true);
    setError(null);
    const err = await onSubmit({ status, reason, note });
    setBusy(false);
    if (err) setError(err);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={driver ? t("reject.title", { name: driver.full_name }) : t("reject.titlePlain")}
      description={t("reject.desc")}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {tc("actions.cancel")}
          </Button>
          <Button variant="danger" onClick={submit} loading={busy} disabled={!reason}>
            {status === "rejected" ? t("reject.rejectDriver") : t("reject.notInterested")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-4 text-[13px]" role="radiogroup">
          <label className="flex cursor-pointer items-center gap-2">
            <input type="radio" name="lost-status" className="accent-primary" checked={status === "rejected"} onChange={() => { setStatus("rejected"); setReason(""); }} />
            {t("reject.rejectedOpt")}
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input type="radio" name="lost-status" className="accent-primary" checked={status === "not_interested"} onChange={() => { setStatus("not_interested"); setReason(""); }} />
            {t("reject.notInterestedOpt")}
          </label>
        </div>
        <Field label={t("reject.reason")} required error={fieldError(error, "reason")}>
          <Select value={reason} onChange={(e) => setReason(e.target.value)} autoFocus>
            <option value="">{t("reject.chooseReason")}</option>
            {reasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("reject.note")}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t("reject.notePlaceholder")} />
        </Field>
        {error && !fieldError(error, "reason") && (
          <div role="alert" className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
            {error.message}
          </div>
        )}
      </div>
    </Modal>
  );
}
